import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardHeader, Alert, Spinner } from '../../components/ui';
import { profileApi } from '../../services/backendApi';
import {
  PUBLIC_PASSPORT_FIELD_OPTIONS,
  buildPublicPassportUrl,
  downloadDataUrl,
  employeeQrFileSlug,
  generatePassportQrPng,
  generatePassportQrSvg,
} from '../../utils';
import type { Profile, PublicPageSettings, ShareableField } from '../../types';

interface Props {
  profile: Profile | null;
}

export const PublicPassportCard: React.FC<Props> = ({ profile }) => {
  const [settings, setSettings] = useState<PublicPageSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    profileApi
      .getPublicPage()
      .then(data => {
        if (!cancelled) setSettings(data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your public Passport settings.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const url = settings?.token ? buildPublicPassportUrl(settings.token) : '';

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    generatePassportQrPng(url, 256)
      .then(dataUrl => {
        if (!cancelled) setPreviewUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const persist = async (next: Partial<Pick<PublicPageSettings, 'public_visible' | 'public_fields'>>) => {
    if (!settings) return;
    const previous = settings;
    const optimistic: PublicPageSettings = {
      ...settings,
      ...next,
    };
    setSettings(optimistic);
    setSaving(true);
    setError(null);
    try {
      const saved = await profileApi.updatePublicPage({
        publicVisible: next.public_visible,
        publicFields: next.public_fields,
      });
      setSettings(saved);
    } catch {
      setSettings(previous);
      setError('Could not save public Passport settings.');
    } finally {
      setSaving(false);
    }
  };

  const toggleField = (field: ShareableField) => {
    if (!settings) return;
    const selected = new Set(settings.public_fields);
    if (selected.has(field)) {
      if (selected.size === 1) return;
      selected.delete(field);
    } else {
      selected.add(field);
    }
    void persist({ public_fields: Array.from(selected) });
  };

  const handleCopy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPng = async () => {
    if (!url || !settings) return;
    const dataUrl = await generatePassportQrPng(url, 1024);
    downloadDataUrl(
      dataUrl,
      `${employeeQrFileSlug(profile?.first_name, profile?.last_name, settings.token)}.png`,
    );
  };

  const handleDownloadSvg = async () => {
    if (!url || !settings) return;
    const svg = await generatePassportQrSvg(url);
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${employeeQrFileSlug(profile?.first_name, profile?.last_name, settings.token)}.svg`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
          </svg>
          <h2 className="text-base font-bold text-gray-900">Public Passport</h2>
        </div>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : !settings ? (
          <Alert variant="danger">{error ?? 'Public Passport is unavailable.'}</Alert>
        ) : (
          <div className="space-y-5">
            {error && (
              <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>
            )}

            <div className="flex items-start justify-between gap-3 min-w-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {settings.public_visible ? 'Visible on your card' : 'Hidden'}
                </p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  This URL never changes. Hide the page without reprinting — the QR still opens, but visitors will see that it is hidden.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={settings.public_visible}
                disabled={saving}
                onClick={() => persist({ public_visible: !settings.public_visible })}
                className={`relative inline-flex h-7 w-12 min-h-0 min-w-[3rem] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  settings.public_visible ? 'bg-brand-600' : 'bg-gray-300'
                } disabled:opacity-50`}
              >
                <span
                  className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                    settings.public_visible ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Shown on this page</p>
              <div className="space-y-2">
                {PUBLIC_PASSPORT_FIELD_OPTIONS.map(option => {
                  const checked = settings.public_fields.includes(option.field);
                  return (
                    <label
                      key={option.field}
                      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        checked ? 'bg-brand-50 border-brand-200' : 'bg-gray-50 border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={saving}
                        onChange={() => toggleField(option.field)}
                        className="h-4 w-4 mt-0.5 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                      />
                      <div>
                        <p className={`text-sm font-semibold ${checked ? 'text-brand-800' : 'text-gray-700'}`}>
                          {option.label}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{option.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Permanent URL
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={url}
                  className="flex-1 px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg text-gray-700 min-w-0 focus:outline-none"
                  onClick={e => (e.target as HTMLInputElement).select()}
                />
                <Button onClick={handleCopy} variant={copied ? 'success' : 'secondary'} size="sm" className="flex-shrink-0">
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Public Passport QR code"
                  className="w-40 h-40 rounded-xl border border-brand-border bg-white"
                />
              ) : (
                <div className="w-40 h-40 rounded-xl border border-brand-border bg-gray-50 animate-pulse" />
              )}
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                <Button variant="primary" size="sm" onClick={handleDownloadPng}>
                  Download PNG
                </Button>
                <Button variant="secondary" size="sm" onClick={handleDownloadSvg}>
                  Download SVG
                </Button>
                <p className="text-xs text-gray-400 leading-relaxed max-w-xs">
                  Print-safe QR (1024px). The code is tied to this URL and cannot be regenerated.
                </p>
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
